import type { TokenResponse } from '@react-oauth/google';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';
import type { FetcherData } from 'types';

import GoogleIcon from '~/assets/icons/Google';
import SignUpMethodEvent from '~/events/SignUpMethodEvent';
import useEnv from '~/hooks/useEnv';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import braze from '~/services/braze.client';
import gtm, { LoginSource } from '~/services/gtm.client';

import type { Props } from '.';

type GoogleLoginSuccessResponse = Omit<
  TokenResponse,
  'error' | 'error_description' | 'error_uri'
>;

export default function GoogleLogin({
  onError,
  onSuccess,
  pagePosition,
  postAuthOptions
}: Props) {
  const { GOOGLE_LOGIN_CLIENT_ID } = useEnv();

  return (
    <GoogleOAuthProvider clientId={GOOGLE_LOGIN_CLIENT_ID}>
      <GoogleLoginButton
        pagePosition={pagePosition}
        onSuccess={onSuccess}
        postAuthOptions={postAuthOptions}
        onError={onError}
      />
    </GoogleOAuthProvider>
  );
}

function GoogleLoginButton({
  onSuccess,
  postAuthOptions,
  onError,
  pagePosition
}: Props) {
  const { t } = useTranslation('common');
  const fetcher = useFetcher<FetcherData>();
  const user = useUser();
  const { isBot } = useRootData();
  const errors = fetcher.data?.errors;

  function onLoginSuccess(response: GoogleLoginSuccessResponse) {
    fetcher.submit(
      {
        action: postAuthOptions?.action ?? '',
        guestId: user.guestId ?? '',
        productId: postAuthOptions?.productId ?? '',
        redirectUrl: postAuthOptions?.redirectUrl ?? '',
        sizes: JSON.stringify(postAuthOptions?.sizes ?? []),
        token: response.access_token
      },
      { action: '/bolt/login/google', method: 'post' }
    );

    gtm.trackedButtonClick({
      buttonName: 'SIGN_IN_GOOGLE',
      pagePosition
    });
  }

  const onSignIn = useCallback(() => {
    gtm.sendLoginEvent(user.id, LoginSource.Google);

    braze.setUser(user.id);
    braze.logLoginEvent();

    onSuccess?.();
  }, [onSuccess, user.id]);

  const errorMessage = t('login.socialLoginError', { name: 'Google' });

  const handleLogin = useGoogleLogin({
    onError: () => onError(errorMessage),
    onNonOAuthError: () => onError(errorMessage),
    onSuccess: onLoginSuccess
  });

  function onLoginClick() {
    onError(null);
    handleLogin();
    new SignUpMethodEvent({
      buttonName: 'SIGN_IN_GOOGLE',
      isBot,
      pagePosition: pagePosition || ''
    }).sendToGtm();
  }

  useEffect(() => {
    if (errors?.message) onError(errors.message);
  }, [errors, onError]);

  useEffect(() => {
    if (fetcher.data?.success) onSignIn();
  }, [fetcher.data, onSignIn]);

  return (
    <button
      className="flex h-12 w-full items-center justify-center gap-2 rounded-lg  bg-concrete px-4 py-2 font-semibold"
      onClick={onLoginClick}
    >
      <GoogleIcon size={22} />
      <span className="hidden md:inline-block">Google</span>
    </button>
  );
}
